// "New entry" sheet for standalone admin calendars — opened by double-clicking empty
// time in a calendar column (or its title-row "+"). Follows the AppointmentFormSheet
// skeleton (right-side sheet, header / scrolling form / footer) and posts straight to
// POST /api/admin/calendars/{id}/entries: no slot, no doctor, optional patient. The
// server publishes the normal appointments.created event; publishCalendarRefresh is
// still called so the grid refetches even when the socket is down.

import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { CalendarDays, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import { createCalendarEntry } from "@/lib/api/admin-calendars";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { hasPermission } from "@/lib/stores/permissions-store";
import {
  buildCalendarEntryBody,
  makeCalendarEntryForm,
  validateCalendarEntryForm,
} from "@/lib/calendars";
import { buildEventColorChoices, formatTime } from "../calendar-utils";
import { LocalOnlyBadge } from "../plato-sync";
import { ColorSwatch, FieldLabel, TimePicker, dateToYmd, isValidHhmm, ymdToDate } from "./form-fields";
import { PatientSearchField } from "./patient-search-field";

function minutesOf(hhmm) {
  if (!isValidHhmm(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function FieldError({ children }) {
  return children ? <p className="text-xs text-destructive">{children}</p> : null;
}

/**
 * `draft`: `{ calendarId, calendarName, colorHex, localOnly, date "YYYY-MM-DD",
 * startTime "HH:MM", endTime "HH:MM" }` — built by the calendar page from the clicked
 * column + minute. The form re-seeds from it every time the sheet opens.
 */
export function CalendarEntryFormSheet({ open, onOpenChange, draft, use12HourFormat = false }) {
  const [form, setForm] = useState(() => makeCalendarEntryForm(draft || {}));
  const [patient, setPatient] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(makeCalendarEntryForm(draft || {}));
      setPatient(null);
      setErrors({});
      setSubmitError("");
    }
    // Re-seed only when the sheet (re)opens — a realtime refetch mid-edit must not
    // clobber the user's draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const calendarName = String(draft?.calendarName || "").trim() || "Calendar";
  const baseColorHex = String(draft?.colorHex || "").trim();
  const colorChoices = useMemo(() => buildEventColorChoices(baseColorHex || undefined), [baseColorHex]);

  const dateLabel = useMemo(() => {
    const parsed = DateTime.fromISO(String(form.date || ""));
    return parsed.isValid ? parsed.toFormat("ccc d LLL yyyy") : "";
  }, [form.date]);

  const timeHint = useMemo(() => {
    const start = minutesOf(form.startTime);
    const end = minutesOf(form.endTime);
    if (start == null || end == null || end <= start) return "";
    const day = DateTime.fromISO(String(form.date || "") || "2000-01-01");
    const base = day.isValid ? day : DateTime.now();
    const startAt = base.set({ hour: Math.floor(start / 60), minute: start % 60 });
    const endAt = base.set({ hour: Math.floor(end / 60), minute: end % 60 });
    return `${formatTime(startAt, { hour12: use12HourFormat })} – ${formatTime(endAt, { hour12: use12HourFormat })} · ${end - start} min`;
  }, [form.date, form.startTime, form.endTime, use12HourFormat]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const calendarId = String(draft?.calendarId || "").trim();
    if (!calendarId) {
      setSubmitError("Pick a calendar first.");
      return;
    }
    // RBAC: direct entries need calendar:manage (the server enforces it too).
    if (!hasPermission("calendar", "manage")) {
      setSubmitError("View only — your access level doesn't allow adding entries.");
      return;
    }
    const nextForm = { ...form, patientId: String(patient?.id || "").trim() };
    const validation = validateCalendarEntryForm(nextForm);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setSubmitError("");
    setSubmitting(true);
    try {
      await createCalendarEntry({ calendarId, body: buildCalendarEntryBody(nextForm) });
      toast.success("Entry added");
      publishCalendarRefresh({ source: "calendar-entry-create", calendarId });
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add the entry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="h-full max-h-dvh w-full gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="relative z-10 shrink-0 border-b border-border bg-background px-6 pb-4 pl-6 pr-14 pt-5 text-left">
          <SheetTitle>New entry</SheetTitle>
          <SheetDescription>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="inline-flex min-w-0 items-center gap-1 text-foreground/85">
                <CalendarDays
                  className="size-3.5 shrink-0 text-muted-foreground"
                  style={baseColorHex ? { color: baseColorHex } : undefined}
                  aria-hidden
                />
                <span className="truncate">{calendarName}</span>
              </span>
              {dateLabel ? <span className="text-muted-foreground">· {dateLabel}</span> : null}
              {draft?.localOnly ? <LocalOnlyBadge /> : null}
            </span>
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
            className="space-y-5 px-6 py-5"
          >
            <div className="space-y-1.5">
              <FieldLabel>
                Title <span className="text-destructive">*</span>
              </FieldLabel>
              <Input
                autoFocus
                value={form.title}
                onChange={(event) => updateField("title", event.target.value)}
                placeholder="What is this entry for?"
                aria-invalid={Boolean(errors.title)}
                className={cn(errors.title && "border-destructive")}
              />
              <FieldError>{errors.title}</FieldError>
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Date</FieldLabel>
              <DatePicker
                date={ymdToDate(form.date)}
                setDate={(next) => updateField("date", dateToYmd(next))}
              />
              <FieldError>{errors.date}</FieldError>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <FieldLabel>Start</FieldLabel>
                <TimePicker
                  value={form.startTime}
                  onChange={(hhmm) => updateField("startTime", hhmm)}
                  error={errors.startTime}
                />
                <FieldError>{errors.startTime}</FieldError>
              </div>
              <div className="space-y-1.5">
                <FieldLabel>End</FieldLabel>
                <TimePicker
                  value={form.endTime}
                  onChange={(hhmm) => updateField("endTime", hhmm)}
                  error={errors.endTime}
                />
                <FieldError>{errors.endTime}</FieldError>
              </div>
            </div>
            {timeHint ? <p className="-mt-3 text-xs text-muted-foreground">{timeHint}</p> : null}

            <div className="space-y-1.5">
              <FieldLabel>Patient (optional)</FieldLabel>
              <PatientSearchField value={patient} onChange={setPatient} disabled={submitting} />
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Colour</FieldLabel>
              <div className="flex flex-wrap items-center gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={() => updateField("colorHex", "")}
                  title="Automatic — follow the calendar colour"
                  aria-pressed={form.colorHex === ""}
                  className={cn(
                    "relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-dashed border-foreground/35 shadow-sm transition-transform hover:scale-110",
                    form.colorHex === "" && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                  )}
                  style={baseColorHex ? { backgroundColor: baseColorHex } : undefined}
                >
                  {form.colorHex === "" ? (
                    <Check
                      className="size-3.5 text-white drop-shadow-[0_1px_1px_rgb(0_0_0/0.6)]"
                      strokeWidth={3}
                      aria-hidden
                    />
                  ) : (
                    <span className="absolute inset-x-0.5 top-1/2 h-px -rotate-45 bg-foreground/35" aria-hidden />
                  )}
                </button>
                {colorChoices.map((hex) => (
                  <ColorSwatch
                    key={hex}
                    hex={hex}
                    selected={form.colorHex === hex}
                    onSelect={() => updateField("colorHex", hex)}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Accent for this entry&apos;s card only — the dashed swatch follows the calendar&apos;s own colour.
              </p>
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Description</FieldLabel>
              <Textarea
                value={form.description}
                onChange={(event) => updateField("description", event.target.value)}
                placeholder="Notes for this entry"
                rows={4}
              />
            </div>

            {submitError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {submitError}
              </div>
            ) : null}
          </form>
        </ScrollArea>

        <div className="shrink-0 border-t border-border bg-background px-6 py-4">
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={submitting} aria-busy={submitting}>
              {submitting ? <Spinner className="mr-2 size-4" /> : null}
              Add entry
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
